import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
} from 'react';

import type { AccessFn } from '@studio/access';
import useLastEqualValue from '@studio/utils/useLastEqualValue';

import type {
  Experience,
  ExperienceConfig,
  VerifiedExperience,
} from '../../utils/experience';
import type { EnabledExperiences } from './utils';

export interface ExperienceContextValue {
  readonly access: AccessFn;
  readonly experience: VerifiedExperience;
  readonly experienceConfig: ExperienceConfig;
  readonly enabledExperiences: EnabledExperiences;
  readonly updatingExperience: boolean;
  readonly updateExperience: (experience: Experience) => void;
}

const ExperienceContext = createContext<ExperienceContextValue | null>(null);
ExperienceContext.displayName = 'ExperienceContext';

export function useExperienceContext(): ExperienceContextValue | null {
  return useContext(ExperienceContext);
}

export interface ExperienceContextProviderProps {
  value: ExperienceContextValue;
  children: ReactNode;
}

export function ExperienceContextProvider({
  value,
  children,
}: ExperienceContextProviderProps): ReactElement {
  const memoizedValue = useLastEqualValue(value);
  return (
    <ExperienceContext.Provider value={memoizedValue}>
      {children}
    </ExperienceContext.Provider>
  );
}
